import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = '/api';

const THEME_STYLES: Record<string, { bg: React.CSSProperties; overlay?: string; useTailwind?: string }> = {
  blue:        { useTailwind: 'from-[#0D47A1] via-[#1565C0] to-[#1976D2]', bg: {} },
  purple:      { useTailwind: 'from-[#4527A0] via-[#6200EA] to-[#7B1FA2]', bg: {} },
  sunset:      { useTailwind: 'from-[#E65100] via-[#F57C00] to-[#EF6C00]', bg: {} },
  green:       { useTailwind: 'from-[#1B5E20] via-[#2E7D32] to-[#388E3C]', bg: {} },
  dark:        { useTailwind: 'from-[#1A1A2E] via-[#16213E] to-[#0F3460]', bg: {} },
  ocean:       { useTailwind: 'from-[#006064] via-[#00838F] to-[#0097A7]', bg: {} },
  red:         { useTailwind: 'from-[#B71C1C] via-[#C62828] to-[#E53935]', bg: {} },
  pink:        { useTailwind: 'from-[#880E4F] via-[#AD1457] to-[#E91E63]', bg: {} },
  warm:        { useTailwind: 'from-[#3E2723] via-[#4E342E] to-[#6D4C41]', bg: {} },
  teal:        { useTailwind: 'from-[#004D40] via-[#00695C] to-[#00796B]', bg: {} },
  aurora:      { bg: { background: 'linear-gradient(135deg,#1A237E 0%,#4A148C 33%,#006064 66%,#1B5E20 100%)' } },
  fire:        { bg: { background: 'linear-gradient(135deg,#BF360C 0%,#E64A19 25%,#FF6F00 60%,#F9A825 100%)' } },
  galaxy:      { bg: { background: 'linear-gradient(135deg,#0A0A0A 0%,#1A0030 40%,#0D47A1 70%,#6200EA 100%)' } },
  lime:        { bg: { background: 'linear-gradient(135deg,#1B5E20 0%,#2E7D32 30%,#00BFA5 70%,#64DD17 100%)' } },
  rose:        { bg: { background: 'linear-gradient(135deg,#880E4F 0%,#AD1457 30%,#FF6F00 70%,#F9A825 100%)' } },
  photo_hands: { bg: { backgroundImage: 'url(https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1200&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }, overlay: 'rgba(13,71,161,0.7)' },
  photo_team:  { bg: { backgroundImage: 'url(https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1200&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }, overlay: 'rgba(98,0,234,0.65)' },
  photo_nature:{ bg: { backgroundImage: 'url(https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }, overlay: 'rgba(27,94,32,0.65)' },
  photo_city:  { bg: { backgroundImage: 'url(https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1200&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }, overlay: 'rgba(10,10,30,0.7)' },
  photo_heart: { bg: { backgroundImage: 'url(https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=1200&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }, overlay: 'rgba(136,14,79,0.65)' },
};

const SKILLS_OPTIONS = [
  '📚 חינוך','💻 טכנולוגיה','🏥 בריאות','⚖️ משפטים',
  '💰 כספים','📣 שיווק','🚗 נהיגה','🍳 בישול','⚽ ספורט','✨ אחר',
];
const AVAILABILITY_OPTIONS = ['ראשון-חמישי','סופי שבוע','ערבים בלבד','בוקר בלבד','גמיש'];

interface OrgInfo {
  orgName: string;
  orgLogo?: string;
  formTitle: string;
  description?: string;
  bulletPoints?: string[];
  ctaText?: string;
  heroImageUrl?: string;
  heroVideoUrl?: string;
  theme?: string;
  font?: string;
  titleSize?: string;
  textColor?: string;
  fields?: string[];
}

const PublicVolunteerPage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const [orgInfo, setOrgInfo] = useState<OrgInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    name: '', phone: '', email: '', birthday: '', area: '', address: '',
    hasVehicle: false, skills: [] as string[], availability: '', notes: '',
    emergencyName: '', emergencyPhone: '', emergencyRelation: '',
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvUploading, setCvUploading] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API_BASE}/volunteers/public/${orgId}`)
      .then(res => { setOrgInfo(res.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [orgId]);

  const fields = orgInfo?.fields || ['name','phone','email','skills','availability','notes'];
  const has = (f: string) => fields.includes(f);

  const toggleSkill = (s: string) => setForm(prev => ({
    ...prev,
    skills: prev.skills.includes(s) ? prev.skills.filter(x => x !== s) : [...prev.skills, s],
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      let cvUrl = '';
      if (cvFile) {
        setCvUploading(true);
        const fd = new FormData();
        fd.append('file', cvFile);
        try {
          const r = await axios.post(`${API_BASE}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          cvUrl = r.data.url || '';
        } catch {}
        setCvUploading(false);
      }
      await axios.post(`${API_BASE}/volunteers/public/${orgId}/register`, {
        ...form,
        cvUrl,
        emergencyContact: form.emergencyName ? { name: form.emergencyName, phone: form.emergencyPhone, relation: form.emergencyRelation } : undefined,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'אירעה שגיאה. נסה שוב.');
    } finally {
      setSubmitting(false);
    }
  };

  const themeKey = orgInfo?.theme || 'blue';
  const themeStyle = THEME_STYLES[themeKey] || THEME_STYLES.blue;
  const hasTailwind = !!themeStyle.useTailwind;
  const tailwindGradient = themeStyle.useTailwind || 'from-[#0D47A1] via-[#1565C0] to-[#1976D2]';
  const bgStyle: React.CSSProperties = hasTailwind ? {} : themeStyle.bg;
  const overlayColor = themeStyle.overlay || '';
  const textColorClass = orgInfo?.textColor === 'dark' ? 'text-gray-900' : 'text-white';
  const textMutedClass = orgInfo?.textColor === 'dark' ? 'text-gray-600' : 'text-white/70';
  const titleSizeClass = orgInfo?.titleSize === 'xl' ? 'text-5xl' : orgInfo?.titleSize === 'lg' ? 'text-4xl' : orgInfo?.titleSize === 'sm' ? 'text-2xl' : 'text-3xl';

  const wrapperClass = hasTailwind
    ? `min-h-screen bg-gradient-to-br ${tailwindGradient}`
    : 'min-h-screen';

  if (loading) return (
    <div className={hasTailwind ? `min-h-screen flex items-center justify-center bg-gradient-to-br ${tailwindGradient}` : 'min-h-screen flex items-center justify-center'} style={hasTailwind ? {} : bgStyle}>
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent" />
    </div>
  );

  if (notFound) return (
    <div className={hasTailwind ? `min-h-screen flex items-center justify-center bg-gradient-to-br ${tailwindGradient}` : 'min-h-screen flex items-center justify-center'} style={hasTailwind ? {} : bgStyle} dir="rtl">
      <div className="text-center text-white">
        <div className="text-6xl mb-4">😕</div>
        <h1 className="text-2xl font-bold">הדף לא נמצא</h1>
      </div>
    </div>
  );

  if (submitted) return (
    <div className={hasTailwind ? `min-h-screen flex items-center justify-center bg-gradient-to-br ${tailwindGradient} p-4` : 'min-h-screen flex items-center justify-center p-4'} style={hasTailwind ? {} : bgStyle} dir="rtl">
      {overlayColor && <div className="fixed inset-0 z-0" style={{ background: overlayColor }} />}
      <div className="relative z-10 bg-white rounded-3xl shadow-2xl p-6 md:p-10 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">✅</span>
        </div>
        <h2 className="text-3xl font-extrabold text-gray-800 mb-3">תודה רבה!</h2>
        <p className="text-gray-500 text-lg">ההרשמה שלך התקבלה בהצלחה.</p>
        <p className="text-gray-400 mt-2">{orgInfo?.orgName} תיצור איתך קשר בקרוב 🙌</p>
      </div>
    </div>
  );

  return (
    <div className={wrapperClass} style={bgStyle} dir="rtl">
      {/* Photo overlay */}
      {overlayColor && <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: overlayColor }} />}

      {/* Hero media */}
      {(orgInfo?.heroVideoUrl || orgInfo?.heroImageUrl) && (
        <div className="relative z-10">
          {orgInfo?.heroVideoUrl ? (
            <video src={orgInfo.heroVideoUrl} className="w-full h-64 object-cover" muted loop autoPlay playsInline />
          ) : (
            <img src={orgInfo.heroImageUrl} alt="hero" className="w-full h-64 object-cover" />
          )}
          <div className="absolute inset-0" style={{ background: hasTailwind ? 'rgba(0,0,0,0.3)' : overlayColor || 'rgba(0,0,0,0.3)' }} />
        </div>
      )}

      <div className="relative z-10 py-10 px-4">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            {orgInfo?.orgLogo && (
              <img src={orgInfo.orgLogo} alt="לוגו" className="h-16 w-auto object-contain mx-auto mb-4 drop-shadow-lg" />
            )}
            <h1 className={`${titleSizeClass} font-extrabold ${textColorClass} mb-3 drop-shadow-sm`}>
              {orgInfo?.formTitle}
            </h1>
            {orgInfo?.description && (
              <p className={`${textMutedClass} text-lg leading-relaxed`}>{orgInfo.description}</p>
            )}
            {orgInfo?.bulletPoints && orgInfo.bulletPoints.filter(Boolean).length > 0 && (
              <ul className={`text-sm mt-3 space-y-1 ${textMutedClass} text-right max-w-xs mx-auto`}>
                {orgInfo.bulletPoints.filter(Boolean).map((b, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="text-green-300 font-bold">✓</span> {b}
                  </li>
                ))}
              </ul>
            )}
            <p className={`${textMutedClass} opacity-60 text-sm mt-2`}>{orgInfo?.orgName}</p>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step >= s ? 'bg-white text-[#1565C0]' : 'bg-white/30 text-white'}`}>{s}</div>
                {s < 2 && <div className={`w-12 h-0.5 ${step >= s+1 ? 'bg-white' : 'bg-white/30'}`} />}
              </div>
            ))}
          </div>

          {/* Form card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className={hasTailwind ? `h-1.5 bg-gradient-to-l ${tailwindGradient}` : 'h-1.5'} style={hasTailwind ? {} : { background: themeStyle.bg.background as string || '#1565C0' }} />
            <form onSubmit={step === 1 ? (e) => { e.preventDefault(); setStep(2); } : handleSubmit} className="p-4 md:p-7 space-y-4">

              {step === 1 && (
                <>
                  <h2 className="font-bold text-lg text-gray-800 mb-4">פרטים אישיים</h2>

                  {has('name') && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1.5">שם מלא *</label>
                      <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                        placeholder="ישראל ישראלי"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none" />
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {has('phone') && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1.5">טלפון</label>
                        <input type="tel" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                          placeholder="050-0000000" dir="ltr"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none" />
                      </div>
                    )}
                    {has('email') && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1.5">אימייל</label>
                        <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                          dir="ltr" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none" />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {has('birthday') && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1.5">תאריך לידה</label>
                        <input type="date" value={form.birthday} onChange={e => setForm(f => ({...f, birthday: e.target.value}))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none" />
                      </div>
                    )}
                    {has('area') && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1.5">אזור מגורים</label>
                        <input value={form.area} onChange={e => setForm(f => ({...f, area: e.target.value}))}
                          placeholder="תל אביב..." className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none" />
                      </div>
                    )}
                  </div>

                  {has('address') && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1.5">כתובת</label>
                      <input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))}
                        placeholder="רחוב, עיר" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none" />
                    </div>
                  )}

                  {has('hasVehicle') && (
                    <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-gray-100 hover:bg-gray-50">
                      <input type="checkbox" checked={form.hasVehicle} onChange={e => setForm(f => ({...f, hasVehicle: e.target.checked}))}
                        className="w-4 h-4 rounded accent-[#1565C0]" />
                      <span className="text-sm font-medium text-gray-700">🚗 יש לי רכב פרטי</span>
                    </label>
                  )}

                  {has('emergencyContact') && (
                    <div className="border-t border-gray-100 pt-4">
                      <h3 className="text-sm font-bold text-gray-500 mb-3">👤 איש קשר לחירום (אופציונלי)</h3>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">שם</label>
                          <input value={form.emergencyName} onChange={e => setForm(f => ({...f, emergencyName: e.target.value}))}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">טלפון</label>
                          <input value={form.emergencyPhone} onChange={e => setForm(f => ({...f, emergencyPhone: e.target.value}))}
                            dir="ltr" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">קשר</label>
                          <input value={form.emergencyRelation} onChange={e => setForm(f => ({...f, emergencyRelation: e.target.value}))}
                            placeholder="הורה, זוג" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none" />
                        </div>
                      </div>
                    </div>
                  )}

                  <button type="submit"
                    className={hasTailwind ? `w-full py-4 bg-gradient-to-l ${tailwindGradient} text-white font-bold rounded-2xl hover:opacity-95 transition-all shadow-lg` : 'w-full py-4 text-white font-bold rounded-2xl hover:opacity-95 transition-all shadow-lg'}
                    style={hasTailwind ? {} : { background: themeStyle.bg.background as string || '#1565C0' }}>
                    המשך ←
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <button type="button" onClick={() => setStep(1)} className="text-gray-400 hover:text-gray-600 text-sm">← חזרה</button>
                    <h2 className="font-bold text-lg text-gray-800">כישורים וזמינות</h2>
                  </div>

                  {has('skills') && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-2">כישורים ותחומי עניין</label>
                      <div className="flex flex-wrap gap-2">
                        {SKILLS_OPTIONS.map(s => (
                          <button key={s} type="button" onClick={() => toggleSkill(s)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${form.skills.includes(s) ? 'bg-[#1565C0] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {has('availability') && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-2">זמינות</label>
                      <div className="grid grid-cols-2 gap-2">
                        {AVAILABILITY_OPTIONS.map(a => (
                          <button key={a} type="button" onClick={() => setForm(f => ({...f, availability: a}))}
                            className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all border ${form.availability === a ? 'bg-[#1565C0] text-white border-[#1565C0]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#1565C0]'}`}>
                            {a}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {has('cv') && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1.5">העלאת קו״ח (אופציונלי)</label>
                      <input type="file" accept=".pdf,.doc,.docx,image/*" onChange={e => setCvFile(e.target.files?.[0] || null)}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none" />
                      {cvFile && <p className="text-xs text-green-600 mt-1">📎 {cvFile.name}</p>}
                      {cvUploading && <p className="text-xs text-gray-400 mt-1">מעלה...</p>}
                    </div>
                  )}

                  {has('notes') && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1.5">ספר לנו קצת על עצמך</label>
                      <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                        rows={3} placeholder="מה מניע אותך להתנדב?"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none resize-none" />
                    </div>
                  )}

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">⚠️ {error}</div>
                  )}

                  <button type="submit" disabled={submitting}
                    className={hasTailwind ? `w-full py-4 bg-gradient-to-l ${tailwindGradient} text-white font-bold rounded-2xl hover:opacity-95 transition-all shadow-lg disabled:opacity-50` : 'w-full py-4 text-white font-bold rounded-2xl hover:opacity-95 transition-all shadow-lg disabled:opacity-50'}
                    style={hasTailwind ? {} : { background: themeStyle.bg.background as string || '#1565C0' }}>
                    {submitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        שולח...
                      </div>
                    ) : (orgInfo?.ctaText || 'שלח הרשמה ✅')}
                  </button>
                </>
              )}
            </form>
          </div>

          <p className="text-center text-white/40 text-xs mt-6">מופעל על ידי Amutech · מערכת ניהול עמותות</p>
        </div>
      </div>
    </div>
  );
};

export default PublicVolunteerPage;
